import React from 'react';
/** Components Import */
import {
  Snackbar as MuiSnackbar,
  Alert as MuiAlert,
  Button,
  Tooltip,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import {
  closeSnackbar,
  clearSnackbarDetail,
} from '../../Reducer/SnackbarReducer';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} {...props} />;
});

function SnackbarBasic({
  /** Snackbar Props */
  action, // node
  anchorOrigin = { vertical: 'top', horizontal: 'center' }, // { horizontal: 'center' | 'left' | 'right', vertical: 'bottom' | 'top' }
  autoHideDuration = 3000, // number
  classes, // object
  ClickAwayListenerProps, // object
  ContentProps, // object
  disableWindowBlurListener = false, // bool
  key = '', // any
  resumeHideDuration, // number
  sx, // object
  /** Alert Props */
  alertAction, // node
  alertChildren, // node
  alertClasses, // object
  alertCloseText = '', // string
  alertColor, // 'error' | 'info' | 'success' | 'warning' | string
  alertComponents, // { CloseButton?: elementType, CloseIcon?: elementType }
  alertComponentsProps, // { closeButton?: object, closeIcon?: object }
  alertIcon, // node
  alertIconMapping, // { error?: node, info?: node, success?: node, warning?: node }
  alertRole = 'alert', // string
  alertSlotProps, // { closeButton?: func | object, closeIcon?: func | object }
  alertSlots, // { closeButton?: elementType, closeIcon?: elementType }
  alertSx, // object
  alertVariant = 'filled', // 'filled' | 'outlined' | 'standard'
  alertOnClose = () => {},
}) {
  const dispatch = useDispatch();
  const { open, message, type, detailData } = useSelector(
    (state) => state.snackbar
  );

  const handleClose = () => {
    dispatch(closeSnackbar());
  };

  const handleClick = () => {
    if (detailData && detailData.length > 0) {
      // detailData가 있으면 커스텀 이벤트 발생
      window.dispatchEvent(
        new CustomEvent('snackbar-detail-click', { detail: detailData })
      );
    }
  };

  const handleDetailButtonClick = (e) => {
    e.stopPropagation(); // 이벤트 버블링 방지
    handleClick();
  };

  // 메시지에서 개행 문자를 처리하는 함수
  const formatMessage = (message) => {
    if (typeof message === 'string' && message.includes('\n')) {
      return message.split('\n').map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < message.split('\n').length - 1 && <br />}
        </React.Fragment>
      ));
    }
    return message;
  };

  // 타입별 hover 색상 설정
  const getHoverBackgroundColor = (severityType) => {
    switch (severityType) {
      case 'success':
        return 'rgba(46, 125, 50, 0.15)'; // 녹색 계열
      case 'error':
        return 'rgba(211, 47, 47, 0.15)'; // 빨간색 계열
      case 'warning':
        return 'rgba(237, 108, 2, 0.15)'; // 주황색 계열
      case 'info':
        return 'rgba(25, 118, 210, 0.15)'; // 파란색 계열
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  };

  // detailData가 있을 때 상세보기 버튼 생성
  const detailActionButton =
    detailData && detailData.length > 0 ? (
      <Tooltip title="상세 내역 보기" arrow>
        <Button
          size="small"
          onClick={handleDetailButtonClick}
          endIcon={<Visibility sx={{ fontSize: '16px' }} />}
          sx={{
            color: 'inherit',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            minWidth: 'auto',
            padding: '4px 8px',
            '&:hover': {
              backgroundColor: getHoverBackgroundColor(type),
            },
          }}
        >
          상세보기
        </Button>
      </Tooltip>
    ) : null;

  return (
    <MuiSnackbar
      action={action}
      anchorOrigin={anchorOrigin}
      autoHideDuration={autoHideDuration}
      classes={classes}
      ClickAwayListenerProps={ClickAwayListenerProps}
      ContentProps={ContentProps}
      disableWindowBlurListener={disableWindowBlurListener}
      key={key}
      message={message}
      open={open}
      resumeHideDuration={resumeHideDuration}
      sx={sx}
      onClose={handleClose}
      TransitionProps={{
        onExited: () => dispatch(clearSnackbarDetail()),
      }}
    >
      <Alert
        action={detailActionButton || alertAction}
        children={alertChildren}
        classes={alertClasses}
        closeText={alertCloseText}
        color={alertColor}
        components={alertComponents}
        componentsProps={alertComponentsProps}
        icon={alertIcon}
        iconMapping={alertIconMapping}
        role={alertRole}
        severity={type}
        slotProps={alertSlotProps}
        slots={alertSlots}
        sx={{
          ...alertSx,
          '& .MuiAlert-message': {
            whiteSpace: 'pre-line', // 개행 문자를 실제 줄바꿈으로 표시
          },
        }}
        variant={alertVariant}
        //onClose={alertOnClose}
      >
        {formatMessage(message)}
      </Alert>
    </MuiSnackbar>
  );
}

export default React.memo(SnackbarBasic);
